import { useEffect, useState, useCallback } from 'react';
import { RootState, AppDispatch } from '../store/store';
import { loadUserProfiles, updateUserProfile, deleteUserProfile } from '../store/admin1Slice';
import { AddEditDeleteBar1 } from '../components_maps/stable/AddEditDeleteBar1';
import { UserProfile1 } from '../types';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogTitle, DialogDescription } from '../components_catalyst/dialog';
import { Button } from '../components_catalyst/button';
import { Input } from '../components_catalyst/input';
import { Select } from '../components_catalyst/select';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { SaveAndRefresh } from '../components_misc/SaveAndRefresh';

// Add MilestoneMap type
type MilestoneMap = {
  id: string;
  uuid: string;
  shortId: string;
  title: string;
  ownerName: string;
};

export function AdminUserProfilesPage() {
  const dispatch = useDispatch<AppDispatch>();
  const userProfiles = useSelector((state: RootState) => state.admin1.userProfiles);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState<UserProfile1 | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredMaps, setFilteredMaps] = useState<MilestoneMap[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (userProfiles.length === 0) {
      dispatch(loadUserProfiles());
    }
  }, [dispatch, userProfiles]);

  const handleMapSearch = async (searchQuery: string) => {
    if (searchQuery.length < 2) return;

    const mapsRef = collection(db, 'milestoneMaps');
    const searchQ = query(
      mapsRef,
      where('title', '>=', searchQuery),
      where('title', '<=', searchQuery + '\uf8ff'),
      limit(10),
    );

    const querySnapshot = await getDocs(searchQ);
    const maps: MilestoneMap[] = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      uuid: doc.id, // Assuming uuid is the same as doc.id
      shortId: doc.data().shortId,
      title: doc.data().title,
      ownerName: doc.data().ownerName,
    }));
    setFilteredMaps(maps);
  };

  const handleEditClick = (userProfile: UserProfile1) => {
    setSelectedUserProfile(userProfile);
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedUserProfile) return;

    try {
      await dispatch(
        updateUserProfile({
          uid: selectedUserProfile.uid,
          updates: selectedUserProfile,
        }),
      );
      setIsEditDialogOpen(false);
      setSelectedUserProfile(null);
    } catch (error) {
      console.error('Error updating user profile:', error);
    }
  };

  const handleDelete = async (uid: string) => {
    await dispatch(deleteUserProfile(uid));
  };

  const handleAccountTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedUserProfile((prev) =>
      prev
        ? {
            ...prev,
            accountType: e.target.value as UserProfile1['accountType'],
          }
        : null,
    );
  };

  const accountTypeOptions = [
    'alpha_pending',
    'alpha_guest',
    'alpha_member',
    'alpha_admin',
    'alpha_deactivated',
  ] as const;

  const handleAddMap = (map: MilestoneMap) => {
    setSelectedUserProfile((prev) => {
      if (!prev) return null;

      const newMap = {
        uuid: map.uuid,
        shortId: map.shortId,
        title: map.title,
        ownerName: prev.userName,
      };

      return {
        ...prev,
        myMilestoneMaps: [...(prev.myMilestoneMaps || []), newMap],
      };
    });
    setSearchQuery('');
    setFilteredMaps([]);
  };

  const handleRefresh = useCallback(async () => {
    await dispatch(loadUserProfiles());
  }, [dispatch]);

  return (
    <div className="p-6" role="region" aria-label="User Profile Management">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">User Profile Management</h1>
        <SaveAndRefresh onRefresh={handleRefresh} />
      </div>
      <div className="overflow-x-auto">
        <table
          className="min-w-full divide-y divide-gray-200"
          role="grid"
          aria-label="User Profiles table"
        >
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                UID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Phone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Account Type
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {userProfiles.map((userProfile) => (
              <tr key={userProfile.uid} className="hover:bg-gray-50" role="row">
                <td className="px-6 py-4 whitespace-nowrap">
                  <AddEditDeleteBar1
                    isVisible={true}
                    showAdd={false}
                    onEdit={() => handleEditClick(userProfile)}
                    onDelete={() => handleDelete(userProfile.uid)}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{userProfile.uid}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {selectedUserProfile === userProfile ? (
                    <div className="space-x-2">
                      <input
                        className="border rounded px-2 py-1"
                        value={selectedUserProfile?.firstName || ''}
                        onChange={(e) =>
                          setSelectedUserProfile((prev) =>
                            prev ? { ...prev, firstName: e.target.value } : null,
                          )
                        }
                        placeholder="First Name"
                        aria-label="First name"
                      />
                      <input
                        className="border rounded px-2 py-1"
                        value={selectedUserProfile?.lastName || ''}
                        onChange={(e) =>
                          setSelectedUserProfile((prev) =>
                            prev ? { ...prev, lastName: e.target.value } : null,
                          )
                        }
                        placeholder="Last Name"
                        aria-label="Last name"
                      />
                    </div>
                  ) : (
                    `${userProfile.firstName || ''} ${userProfile.lastName || ''}`
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {selectedUserProfile === userProfile ? (
                    <input
                      className="border rounded px-2 py-1"
                      value={selectedUserProfile?.email?.[0] || ''}
                      onChange={(e) =>
                        setSelectedUserProfile((prev) =>
                          prev ? { ...prev, email: [e.target.value] } : null,
                        )
                      }
                    />
                  ) : (
                    userProfile.email?.[0]
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {selectedUserProfile === userProfile ? (
                    <input
                      className="border rounded px-2 py-1"
                      value={selectedUserProfile?.phone?.[0] || ''}
                      onChange={(e) =>
                        setSelectedUserProfile((prev) =>
                          prev ? { ...prev, phone: [e.target.value] } : null,
                        )
                      }
                    />
                  ) : (
                    userProfile.phone?.[0]
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {selectedUserProfile === userProfile ? (
                    <div className="flex space-x-2">
                      <Select
                        value={selectedUserProfile?.accountType || ''}
                        onChange={handleAccountTypeChange}
                      >
                        {accountTypeOptions.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </Select>
                      <Button onClick={handleSaveEdit}>Save</Button>
                    </div>
                  ) : (
                    userProfile.accountType
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        aria-label="Edit user profile details"
      >
        <div className="p-6">
          <DialogTitle>Edit User Profile</DialogTitle>
          <DialogDescription>Update user profile information below</DialogDescription>

          <div className="mt-6 space-y-6" role="form" aria-label="User profile edit form">
            {/* Basic Information */}
            <div className="space-y-4" role="group" aria-labelledby="basic-info-title">
              <h3 id="basic-info-title" className="font-medium">
                Basic Information
              </h3>

              <div>
                <label htmlFor="userName" className="block text-sm font-medium text-gray-700">
                  User Name
                </label>
                <Input
                  id="userName"
                  value={selectedUserProfile?.userName || ''}
                  onChange={(e) =>
                    setSelectedUserProfile((prev) =>
                      prev ? { ...prev, userName: e.target.value } : null,
                    )
                  }
                  aria-label="User name"
                />
              </div>

              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <Input
                  id="firstName"
                  value={selectedUserProfile?.firstName || ''}
                  onChange={(e) =>
                    setSelectedUserProfile((prev) =>
                      prev ? { ...prev, firstName: e.target.value } : null,
                    )
                  }
                  aria-label="First name"
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <Input
                  id="lastName"
                  value={selectedUserProfile?.lastName || ''}
                  onChange={(e) =>
                    setSelectedUserProfile((prev) =>
                      prev ? { ...prev, lastName: e.target.value } : null,
                    )
                  }
                  aria-label="Last name"
                />
              </div>

              {/* Email Addresses Section */}
              <div role="group" aria-labelledby="email-addresses-title">
                <label
                  id="email-addresses-title"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email Addresses
                </label>
                {selectedUserProfile?.email?.map((email, index) => (
                  <div key={index} className="flex gap-2 mt-2">
                    <Input
                      value={email}
                      onChange={(e) => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newEmails = [...(prev.email || [])];
                          newEmails[index] = e.target.value;
                          return { ...prev, email: newEmails };
                        });
                      }}
                      aria-label={`Email address ${index + 1}`}
                    />
                    <Button
                      color="red"
                      onClick={() => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newEmails = [...(prev.email || [])];
                          newEmails.splice(index, 1);
                          return { ...prev, email: newEmails };
                        });
                      }}
                      aria-label={`Remove email address ${index + 1}`}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  color="light"
                  className="mt-2"
                  onClick={() => {
                    setSelectedUserProfile((prev) =>
                      prev ? { ...prev, email: [...(prev.email || []), ''] } : null,
                    );
                  }}
                  aria-label="Add new email address"
                >
                  Add Email
                </Button>
              </div>

              {/* Phone Numbers Section */}
              <div role="group" aria-labelledby="phone-numbers-title">
                <label id="phone-numbers-title" className="block text-sm font-medium text-gray-700">
                  Phone Numbers
                </label>
                {selectedUserProfile?.phone?.map((phone, index) => (
                  <div key={index} className="flex gap-2 mt-2">
                    <Input
                      value={phone}
                      onChange={(e) => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newPhones = [...(prev.phone || [])];
                          newPhones[index] = e.target.value;
                          return { ...prev, phone: newPhones };
                        });
                      }}
                      aria-label={`Phone number ${index + 1}`}
                    />
                    <Button
                      color="red"
                      onClick={() => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newPhones = [...(prev.phone || [])];
                          newPhones.splice(index, 1);
                          return { ...prev, phone: newPhones };
                        });
                      }}
                      aria-label={`Remove phone number ${index + 1}`}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  color="light"
                  className="mt-2"
                  onClick={() => {
                    setSelectedUserProfile((prev) =>
                      prev ? { ...prev, phone: [...(prev.phone || []), ''] } : null,
                    );
                  }}
                  aria-label="Add new phone number"
                >
                  Add Phone
                </Button>
              </div>

              {/* Account Type Section */}
              <div>
                <label htmlFor="accountType" className="block text-sm font-medium text-gray-700">
                  Account Type
                </label>
                <Select
                  id="accountType"
                  value={selectedUserProfile?.accountType || ''}
                  onChange={handleAccountTypeChange}
                  aria-label="Account type"
                >
                  {accountTypeOptions.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Organizations Section */}
            <div className="space-y-4" role="group" aria-labelledby="organizations-title">
              <h3 id="organizations-title" className="font-medium">
                Organizations
              </h3>
              {selectedUserProfile?.orgs?.map((org, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    placeholder="Org UUID"
                    value={org.orgUuid}
                    onChange={(e) => {
                      setSelectedUserProfile((prev) => {
                        if (!prev) return null;
                        const newOrgs = [...(prev.orgs || [])];
                        newOrgs[index] = { ...newOrgs[index], orgUuid: e.target.value };
                        return { ...prev, orgs: newOrgs };
                      });
                    }}
                    aria-label={`Organization ${index + 1} UUID`}
                  />
                  <Input
                    placeholder="Org Name"
                    value={org.name}
                    onChange={(e) => {
                      setSelectedUserProfile((prev) => {
                        if (!prev) return null;
                        const newOrgs = [...(prev.orgs || [])];
                        newOrgs[index] = { ...newOrgs[index], name: e.target.value };
                        return { ...prev, orgs: newOrgs };
                      });
                    }}
                    aria-label={`Organization ${index + 1} name`}
                  />
                  <Button
                    color="red"
                    onClick={() => {
                      setSelectedUserProfile((prev) => {
                        if (!prev) return null;
                        const newOrgs = [...(prev.orgs || [])];
                        newOrgs.splice(index, 1);
                        return { ...prev, orgs: newOrgs };
                      });
                    }}
                    aria-label={`Remove organization ${index + 1}`}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                color="light"
                onClick={() => {
                  setSelectedUserProfile((prev) =>
                    prev
                      ? {
                          ...prev,
                          orgs: [...(prev.orgs || []), { orgUuid: '', name: '' }],
                        }
                      : null,
                  );
                }}
                aria-label="Add new organization"
              >
                Add Organization
              </Button>
            </div>

            {/* My Milestone Maps Section */}
            <div className="space-y-4">
              <h3 className="font-medium">My Milestone Maps</h3>
              {selectedUserProfile?.myMilestoneMaps?.map((map, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex justify-end">
                    <Button
                      color="red"
                      onClick={() => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newMaps = [...(prev.myMilestoneMaps || [])];
                          newMaps.splice(index, 1);
                          return { ...prev, myMilestoneMaps: newMaps };
                        });
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">UUID</label>
                    <Input
                      placeholder="UUID"
                      value={map.uuid}
                      onChange={(e) => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newMaps = [...(prev.myMilestoneMaps || [])];
                          newMaps[index] = { ...newMaps[index], uuid: e.target.value };
                          return { ...prev, myMilestoneMaps: newMaps };
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Short ID</label>
                    <Input
                      placeholder="Short ID"
                      value={map.shortId}
                      onChange={(e) => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newMaps = [...(prev.myMilestoneMaps || [])];
                          newMaps[index] = { ...newMaps[index], shortId: e.target.value };
                          return { ...prev, myMilestoneMaps: newMaps };
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <Input
                      placeholder="Title"
                      value={map.title}
                      onChange={(e) => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newMaps = [...(prev.myMilestoneMaps || [])];
                          newMaps[index] = { ...newMaps[index], title: e.target.value };
                          return { ...prev, myMilestoneMaps: newMaps };
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Owner Name</label>
                    <Input
                      placeholder="Owner Name"
                      value={map.ownerName}
                      onChange={(e) => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newMaps = [...(prev.myMilestoneMaps || [])];
                          newMaps[index] = { ...newMaps[index], ownerName: e.target.value };
                          return { ...prev, myMilestoneMaps: newMaps };
                        });
                      }}
                    />
                  </div>
                </div>
              ))}
              <Button
                color="light"
                onClick={() => {
                  setSelectedUserProfile((prev) =>
                    prev
                      ? {
                          ...prev,
                          myMilestoneMaps: [
                            ...(prev.myMilestoneMaps || []),
                            { uuid: '', shortId: '', title: '', ownerName: prev.userName },
                          ],
                        }
                      : null,
                  );
                }}
              >
                Add Map
              </Button>
            </div>

            {/* Accessed Milestone Maps Section */}
            <div className="space-y-4">
              <h3 className="font-medium">Accessed Milestone Maps</h3>
              {selectedUserProfile?.accessedMilestoneMaps?.map((map, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <Button
                      color="zinc"
                      onClick={() => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newMaps = [...(prev.accessedMilestoneMaps || [])];
                          newMaps[index] = { ...newMaps[index], pinned: !newMaps[index].pinned };
                          return { ...prev, accessedMilestoneMaps: newMaps };
                        });
                      }}
                    >
                      {map.pinned ? 'Unpin' : 'Pin'}
                    </Button>
                    <Button
                      color="red"
                      onClick={() => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newMaps = [...(prev.accessedMilestoneMaps || [])];
                          newMaps.splice(index, 1);
                          return { ...prev, accessedMilestoneMaps: newMaps };
                        });
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">UUID</label>
                    <Input
                      placeholder="UUID"
                      value={map.uuid}
                      onChange={(e) => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newMaps = [...(prev.accessedMilestoneMaps || [])];
                          newMaps[index] = { ...newMaps[index], uuid: e.target.value };
                          return { ...prev, accessedMilestoneMaps: newMaps };
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Short ID</label>
                    <Input
                      placeholder="Short ID"
                      value={map.shortId}
                      onChange={(e) => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newMaps = [...(prev.accessedMilestoneMaps || [])];
                          newMaps[index] = { ...newMaps[index], shortId: e.target.value };
                          return { ...prev, accessedMilestoneMaps: newMaps };
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <Input
                      placeholder="Title"
                      value={map.title}
                      onChange={(e) => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newMaps = [...(prev.accessedMilestoneMaps || [])];
                          newMaps[index] = { ...newMaps[index], title: e.target.value };
                          return { ...prev, accessedMilestoneMaps: newMaps };
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Owner Name</label>
                    <Input
                      placeholder="Owner Name"
                      value={map.ownerName}
                      onChange={(e) => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newMaps = [...(prev.accessedMilestoneMaps || [])];
                          newMaps[index] = { ...newMaps[index], ownerName: e.target.value };
                          return { ...prev, accessedMilestoneMaps: newMaps };
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Last Accessed</label>
                    <Input
                      type="datetime-local"
                      value={map.lastAccessed?.split('.')[0] || ''}
                      onChange={(e) => {
                        setSelectedUserProfile((prev) => {
                          if (!prev) return null;
                          const newMaps = [...(prev.accessedMilestoneMaps || [])];
                          newMaps[index] = { ...newMaps[index], lastAccessed: e.target.value };
                          return { ...prev, accessedMilestoneMaps: newMaps };
                        });
                      }}
                    />
                  </div>
                </div>
              ))}
              <Button
                color="light"
                onClick={() => {
                  setSelectedUserProfile((prev) =>
                    prev
                      ? {
                          ...prev,
                          accessedMilestoneMaps: [
                            ...(prev.accessedMilestoneMaps || []),
                            {
                              uuid: '',
                              shortId: '',
                              title: '',
                              ownerName: prev.userName,
                              lastAccessed: new Date().toISOString(),
                              pinned: false,
                            },
                          ],
                        }
                      : null,
                  );
                }}
              >
                Add Map
              </Button>
            </div>

            {/* Dialog Actions */}
            <div className="mt-6 flex justify-end space-x-3">
              <Button
                color="zinc"
                onClick={() => setIsEditDialogOpen(false)}
                aria-label="Cancel editing"
              >
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} aria-label="Save all changes">
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
